/**
 * Bons cadeaux : émettre un code payé d'avance, suivre ceux qui circulent.
 *
 * Le code complet n'apparaît QU'À L'ÉMISSION — la liste ne le remontre jamais.
 * Un bon vivant est un titre au porteur : l'afficher dans un tableau consultable
 * le rendrait utilisable par qui passe derrière l'écran.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

interface Bon {
  id: string;
  code_masque: string;
  amount_cents: number;
  status: 'issued' | 'redeemed' | 'cancelled';
  issued_at: string;
  issued_by_name?: string | null;
  sale_reference?: string | null;
  recipient_label?: string | null;
  expires_at?: string | null;
  redeemed_at?: string | null;
}

const STATUT: Record<Bon['status'], { label: string; classe: string }> = {
  issued: { label: 'En circulation', classe: 'bg-amber-100 text-amber-800' },
  redeemed: { label: 'Utilisé', classe: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Annulé', classe: 'bg-gray-200 text-gray-600' },
};

const dateFr = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('fr-BE', { timeZone: 'Europe/Brussels' }) : '—';

export default function GiftVouchersPage() {
  const navigate = useNavigate();

  const [bons, setBons] = useState<Bon[]>([]);
  const [chargement, setChargement] = useState(true);

  const [montant, setMontant] = useState('30');
  const [beneficiaire, setBeneficiaire] = useState('');
  const [ticket, setTicket] = useState('');
  const [validite, setValidite] = useState('12');
  const [emission, setEmission] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /** Le code fraîchement émis, affiché en grand — c'est ce qu'on recopie. */
  const [codeEmis, setCodeEmis] = useState<{ code: string; montant: string } | null>(null);

  const charger = useCallback(async () => {
    try {
      setChargement(true);
      setBons(await api.get<Bon[]>('/gift-vouchers'));
    } catch (e: any) {
      setErreur(e?.response?.data?.message || 'Impossible de charger les bons.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const emettre = async () => {
    const cents = Math.round(parseFloat(montant.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setErreur('Montant invalide.');
      return;
    }
    setEmission(true);
    setErreur(null);
    try {
      const bon = await api.post<{ code_formate: string; amount_cents: number }>('/gift-vouchers', {
        amount_cents: cents,
        recipient_label: beneficiaire.trim() || undefined,
        sale_reference: ticket.trim() || undefined,
        valid_months: validite ? parseInt(validite, 10) : undefined,
      });
      setCodeEmis({
        code: bon.code_formate,
        montant: (bon.amount_cents / 100).toFixed(2),
      });
      setBeneficiaire('');
      setTicket('');
      await charger();
    } catch (e: any) {
      setErreur(e?.response?.data?.message || "L'émission a échoué.");
    } finally {
      setEmission(false);
    }
  };

  const annuler = async (bon: Bon) => {
    const motif = window.prompt(
      `Annuler le bon ${bon.code_masque} (${(bon.amount_cents / 100).toFixed(2)} €) ?\n\nMotif :`,
    );
    if (!motif || !motif.trim()) return;
    try {
      await api.post(`/gift-vouchers/${bon.id}/cancel`, { motif: motif.trim() });
      await charger();
    } catch (e: any) {
      setErreur(e?.response?.data?.message || "L'annulation a échoué.");
    }
  };

  const enCirculation = bons.filter((b) => b.status === 'issued');
  const engagement = enCirculation.reduce((s, b) => s + b.amount_cents, 0);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900 mb-4">
        ← Retour
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-1">Bons cadeaux</h1>
      <p className="text-gray-500 mb-6">
        Un code payé d'avance, utilisable une seule fois, qui se transforme en FOLIES sur le compte
        de qui le présente.
      </p>

      {erreur && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6 text-red-800">
          {erreur}
        </div>
      )}

      {/* Le code, affiché une seule fois */}
      {codeEmis && (
        <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-6 mb-6">
          <div className="text-green-800 font-bold mb-2">
            🎁 Bon de {codeEmis.montant} € créé — recopiez ce code sur le carton :
          </div>
          <div className="font-mono text-2xl sm:text-3xl font-bold tracking-widest text-green-900 bg-white rounded-xl p-4 text-center select-all break-all">
            {codeEmis.code}
          </div>
          <div className="text-sm text-green-700 mt-3">
            Il ne sera plus jamais affiché en entier. S'il est perdu avant d'être recopié, annulez ce
            bon et émettez-en un autre.
          </div>
          <button
            onClick={() => setCodeEmis(null)}
            className="mt-4 bg-green-700 text-white px-6 py-2 rounded-xl font-bold hover:bg-green-800"
          >
            J'ai recopié le code
          </button>
        </div>
      )}

      {/* Émission */}
      <div className="bg-white rounded-2xl shadow p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Émettre un bon</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Montant (€)</span>
            <input
              type="text"
              inputMode="decimal"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              className="mt-1 w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Pour qui (facultatif)</span>
            <input
              type="text"
              value={beneficiaire}
              onChange={(e) => setBeneficiaire(e.target.value)}
              placeholder="Mme Dupont"
              className="mt-1 w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">N° de ticket</span>
            <input
              type="text"
              value={ticket}
              onChange={(e) => setTicket(e.target.value)}
              placeholder="SALE-20260912-…"
              className="mt-1 w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Validité (mois)</span>
            <input
              type="text"
              inputMode="numeric"
              value={validite}
              onChange={(e) => setValidite(e.target.value)}
              className="mt-1 w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none"
            />
          </label>
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Le n° de ticket relie l'argent encaissé au bon émis — c'est ce qui permet au comptable de
          s'y retrouver. La validité porte sur le BON : une fois les FOLIES créditées, elles
          n'expirent plus.
        </p>
        <button
          onClick={emettre}
          disabled={emission}
          className="mt-4 bg-green-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-green-700 disabled:bg-gray-300"
        >
          {emission ? 'Création…' : 'Créer le bon'}
        </button>
      </div>

      {/* Engagement */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 mb-6">
        <div className="text-amber-900">
          <span className="font-bold text-2xl">{(engagement / 100).toFixed(2)} €</span>{' '}
          de bons en circulation ({enCirculation.length}) — de la marchandise déjà payée, pas encore
          servie.
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-2xl shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Historique</h2>
        {chargement ? (
          <p className="text-gray-500">Chargement…</p>
        ) : bons.length === 0 ? (
          <p className="text-gray-500">Aucun bon émis pour l'instant.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Code</th>
                  <th className="py-2 pr-4">Montant</th>
                  <th className="py-2 pr-4">État</th>
                  <th className="py-2 pr-4">Émis le</th>
                  <th className="py-2 pr-4">Pour</th>
                  <th className="py-2 pr-4">Expire</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {bons.map((b) => (
                  <tr key={b.id} className="border-b last:border-0">
                    <td className="py-3 pr-4 font-mono whitespace-nowrap">{b.code_masque}</td>
                    <td className="py-3 pr-4 font-bold whitespace-nowrap">
                      {(b.amount_cents / 100).toFixed(2)} €
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-bold ${STATUT[b.status].classe}`}
                      >
                        {STATUT[b.status].label}
                      </span>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">{dateFr(b.issued_at)}</td>
                    <td className="py-3 pr-4">{b.recipient_label || '—'}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{dateFr(b.expires_at)}</td>
                    <td className="py-3 text-right">
                      {b.status === 'issued' && (
                        <button
                          onClick={() => annuler(b)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Annuler
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
